import { useRouteContext } from '@tanstack/react-router'
import { buildRecordMutation } from '@/utils/recordMutation'
import { LeaDataModel, EffectiveDateRecord, MutationResponse, GlobalStateModel } from '@/models'

export type FAMI = {
    Id: string
    ReportingLEA: string
    PreschoolCode: string
    ProgramYear: string
    LocalFamilyId: string
    CSPPID: string
    LocalChildId: string
    HeadOfHousehold1LastName: string
    HeadOfHousehold1FirstName: string
    HeadOfHousehold1MiddleName: string
    HeadOfHousehold1HighestEducationLevel: string
    HeadOfHousehold2LastName: string | undefined
    HeadOfHousehold2FirstName: string | undefined
    HeadOfHousehold2MiddleName: string | undefined
    HeadOfHousehold2HighestEducationLevel: string | undefined
    ResidentialAddress1: string
    ResidentialAddress2: string
    ResidentialCity: string
    ResidentialState: string
    ResidentialZip: string
    MailingAddress1: string
    MailingAddress2: string
    MailingCity: string
    MailingState: string
    MailingZip: string
    HeadOfHouseholdWrittenCommunicationPreference: string
    HeadOfHouseholdVerbalCommunicationPreference: string
    FamilyIncome: number | string
    HeadOfHousehold1NeedForService1: string
    HeadOfHousehold1NeedForService2: string
    HeadOfHousehold1NeedForService3: string
    HeadOfHousehold1NeedForService4: string
    HeadOfHousehold1NeedForService5: string
    HeadOfHousehold1NeedForService6: string
    HeadOfHousehold1NeedForService7: string
    HeadOfHousehold1NeedForService8: string
    HeadOfHousehold1NeedForService9: string
    HeadOfHousehold2NeedForService1: string
    HeadOfHousehold2NeedForService2: string
    HeadOfHousehold2NeedForService3: string
    HeadOfHousehold2NeedForService4: string
    HeadOfHousehold2NeedForService5: string
    HeadOfHousehold2NeedForService6: string
    HeadOfHousehold2NeedForService7: string
    HeadOfHousehold2NeedForService8: string
    HeadOfHousehold2NeedForService9: string
    CALWorksRecipient: string
    FamilySize: number | string
} & EffectiveDateRecord

export const FAMI_TEMPLATE: FAMI = {
    Id: '',
    ReportingLEA: '',
    PreschoolCode: '',
    ProgramYear: '',
    LocalFamilyId: '',
    CSPPID: '',
    LocalChildId: '',
    HeadOfHousehold1LastName: '',
    HeadOfHousehold1FirstName: '',
    HeadOfHousehold1MiddleName: '',
    HeadOfHousehold1HighestEducationLevel: '',
    HeadOfHousehold2LastName: '',
    HeadOfHousehold2FirstName: '',
    HeadOfHousehold2MiddleName: '',
    HeadOfHousehold2HighestEducationLevel: '',
    ResidentialAddress1: '',
    ResidentialAddress2: '',
    ResidentialCity: '',
    ResidentialState: '',
    ResidentialZip: '',
    MailingAddress1: '',
    MailingAddress2: '',
    MailingCity: '',
    MailingState: '',
    MailingZip: '',
    HeadOfHouseholdWrittenCommunicationPreference: '',
    HeadOfHouseholdVerbalCommunicationPreference: '',
    FamilyIncome: '',
    HeadOfHousehold1NeedForService1: '',
    HeadOfHousehold1NeedForService2: '',
    HeadOfHousehold1NeedForService3: '',
    HeadOfHousehold1NeedForService4: '',
    HeadOfHousehold1NeedForService5: '',
    HeadOfHousehold1NeedForService6: '',
    HeadOfHousehold1NeedForService7: '',
    HeadOfHousehold1NeedForService8: '',
    HeadOfHousehold1NeedForService9: '',
    HeadOfHousehold2NeedForService1: '',
    HeadOfHousehold2NeedForService2: '',
    HeadOfHousehold2NeedForService3: '',
    HeadOfHousehold2NeedForService4: '',
    HeadOfHousehold2NeedForService5: '',
    HeadOfHousehold2NeedForService6: '',
    HeadOfHousehold2NeedForService7: '',
    HeadOfHousehold2NeedForService8: '',
    HeadOfHousehold2NeedForService9: '',
    CALWorksRecipient: '',
    FamilySize: '',
    EffectiveStartDate: '',
    EffectiveEndDate: ''
}

export function FAMI_Key(x: FAMI): string {
    return x.LocalFamilyId
}

function toIsoDate(value: string | null | undefined): string {
    if (!value) return ''
    const trimmed = String(value).trim()
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed
    const mdy = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
    if (mdy) {
        const [, m, d, y] = mdy
        return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`
    }
    return trimmed
}

export function asSerializeable(fami: FAMI) {
    return {
        'ReportingLEA': fami.ReportingLEA,
        'PreschoolCode': fami.PreschoolCode,
        'ProgramYear': fami.ProgramYear,
        'LocalFamilyId': fami.LocalFamilyId,
        'CSPPID': fami.CSPPID,
        'LocalChildId': fami.LocalChildId,
        'HeadOfHousehold1FirstName': fami.HeadOfHousehold1FirstName,
        'HeadOfHousehold1MiddleName': fami.HeadOfHousehold1MiddleName,
        'HeadOfHousehold1LastName': fami.HeadOfHousehold1LastName,
        'HeadOfHousehold2LastName': fami.HeadOfHousehold2LastName,
        'HeadOfHousehold2FirstName': fami.HeadOfHousehold2FirstName,
        'HeadOfHousehold2MiddleName': fami.HeadOfHousehold2MiddleName,
        'HeadOfHousehold1HighestEducationLevel': fami.HeadOfHousehold1HighestEducationLevel,
        'HeadOfHousehold2HighestEducationLevel': fami.HeadOfHousehold2HighestEducationLevel,
        'ResidentialAddress1': fami.ResidentialAddress1,
        'ResidentialAddress2': fami.ResidentialAddress2,
        'ResidentialCity': fami.ResidentialCity,
        'ResidentialState': fami.ResidentialState,
        'ResidentialZip': fami.ResidentialZip,
        'MailingAddress1': fami.MailingAddress1,
        'MailingAddress2': fami.MailingAddress2,
        'MailingCity': fami.MailingCity,
        'MailingState': fami.MailingState,
        'MailingZip': fami.MailingZip,
        'HeadOfHouseholdWrittenCommunicationPreference': fami.HeadOfHouseholdWrittenCommunicationPreference,
        'HeadOfHouseholdVerbalCommunicationPreference': fami.HeadOfHouseholdVerbalCommunicationPreference,
        'FamilyIncome': Number(fami.FamilyIncome),
        'HeadOfHousehold1NeedForService1': fami.HeadOfHousehold1NeedForService1,
        'HeadOfHousehold1NeedForService2': fami.HeadOfHousehold1NeedForService2,
        'HeadOfHousehold1NeedForService3': fami.HeadOfHousehold1NeedForService3,
        'HeadOfHousehold1NeedForService4': fami.HeadOfHousehold1NeedForService4,
        'HeadOfHousehold1NeedForService5': fami.HeadOfHousehold1NeedForService5,
        'HeadOfHousehold1NeedForService6': fami.HeadOfHousehold1NeedForService6,
        'HeadOfHousehold1NeedForService7': fami.HeadOfHousehold1NeedForService7,
        'HeadOfHousehold1NeedForService8': fami.HeadOfHousehold1NeedForService8,
        'HeadOfHousehold1NeedForService9': fami.HeadOfHousehold1NeedForService9,
        'HeadOfHousehold2NeedForService1': fami.HeadOfHousehold2NeedForService1,
        'HeadOfHousehold2NeedForService2': fami.HeadOfHousehold2NeedForService2,
        'HeadOfHousehold2NeedForService3': fami.HeadOfHousehold2NeedForService3,
        'HeadOfHousehold2NeedForService4': fami.HeadOfHousehold2NeedForService4,
        'HeadOfHousehold2NeedForService5': fami.HeadOfHousehold2NeedForService5,
        'HeadOfHousehold2NeedForService6': fami.HeadOfHousehold2NeedForService6,
        'HeadOfHousehold2NeedForService7': fami.HeadOfHousehold2NeedForService7,
        'HeadOfHousehold2NeedForService8': fami.HeadOfHousehold2NeedForService8,
        'HeadOfHousehold2NeedForService9': fami.HeadOfHousehold2NeedForService9,
        'CALWorksRecipient': fami.CALWorksRecipient,
        'FamilySize': Number(fami.FamilySize),
        'EffectiveStartDate': toIsoDate(fami.EffectiveStartDate),
        'EffectiveEndDate': toIsoDate(fami.EffectiveEndDate),
    }
}

export function mutationFunctions(_leaData: LeaDataModel, setSubmitResult: (resp: MutationResponse<FAMI>) => void) {
    const leaContext = useRouteContext({ from: '/$leaid' })
    return buildRecordMutation<FAMI>(leaContext as GlobalStateModel, setSubmitResult, asSerializeable, 'EditFamilyAjax')
}